import { Controller } from "@hotwired/stimulus";
import Rails from "@rails/ujs";

/**
 * 車両のお気に入りボタンを制御する。
 *
 * 使用例:
 * ```erb
 * <button class="btn btn-icon rounded-circle form-check-like">
 *   <%
 *     check_box_id = "exhibited_cars_#{exhibited_car.id}"
 *   %>
 *   <%= check_box_tag exhibited_car_likes_path(exhibited_car),
 *       exhibited_car.id,
 *       exhibited_car.liked?(current_user, GuestExhibitedCarLikeCookie.new(cookies)),
 *       data: {
 *         controller: "exhibited-car-favorites",
 *         exhibited_cars: exhibited_car.id,
 *       },
 *       id: check_box_id %>
 *   <%= label_tag check_box_id, "", class: "icon icon-heart" %>
 * </button>
 * ```
 */
export default class extends Controller {
  connect() {
    // checkbox name に URL 入れておく
    this.src = this.element.getAttribute("name");
    if (this.element.dataset.countSelector) {
      this.count = document.querySelector(this.element.dataset.countSelector);
    }
    this.modalTriggerSelector = this.element.dataset.modalTriggerSelector;
    // モーダル再表示抑止用（他バナーと同じ {key}_modal_shown Cookie の仕組み）
    this.modalSuppressionUrl = this.element.dataset.modalSuppressionUrl;
    this.modalSuppressionCookie = this.element.dataset.modalSuppressionCookie;
    // 同一ページ内での再表示を防ぐフラグ（Cookie 設定は非同期のため）
    this.modalShownOnThisPage = false;
    this.bind();
  }

  bind() {
    this.element.addEventListener("change", () => {
      // 成功時の UI 確定と失敗時のチェック状態の復元は request() に委ねる
      this.request(this.src, this.element.checked);
    });
  }

  /**
   * ページ内の同じ車両を表示しているお気に入りボタンのチェック状態を連動させる。
   * 連動対象の判定は relatedButtons() に委譲する。
   * @param {boolean} faved お気に入り登録/解除フラグ
   * @returns
   */
  sync(faved) {
    this.relatedButtons().forEach((button) => {
      button.checked = faved;
    });
  }

  /**
   * 同一車両（同じ `data-exhibited-cars`）の連動ボタンを返す。
   * 連動・連打防止のグループ判定に使う。
   * `data-exhibited-cars` がない場合は車両を特定できないため自身のみを返す
   * （value で代用すると別車両のボタンを巻き込むため使わない）。
   * @returns {HTMLElement[]}
   */
  relatedButtons() {
    const carId = this.element.dataset.exhibitedCars;
    if (!carId) return [this.element];

    const group = document.querySelectorAll(`[data-exhibited-cars="${carId}"]`);
    return Array.from(new Set([this.element, ...group]));
  }

  /**
   * change 発火時点でブラウザがチェック状態をトグル済みなので、その値を faved として受け取る。
   * 送信前に連動ボタンをまとめて無効化（連打防止）し、
   * 確定（成功時）・復元（失敗時）・再有効化（完了時）は各コールバックで行う。
   * @param {string} src "/exhibited_cars/:id/likes"形式のURL
   * @param {boolean} faved お気に入り登録/解除フラグ
   * @returns
   */
  request(src, faved) {
    const method = faved ? "POST" : "DELETE";

    // 連打で複数リクエストが in-flight になるのを防ぐ。
    // 同一車両の連動ボタン経由で並行リクエストが撃たれるのも防ぐためまとめて無効化する。
    const buttons = this.relatedButtons();
    buttons.forEach((button) => {
      button.disabled = true;
    });

    Rails.ajax({
      type: method,
      url: src,
      // https://github.com/rails/rails/commit/ad3a47759e67a411f3534309cdd704f12f6930a7#diff-5add1333e3835d377f5710902a10f6c97e4fceb2e29860bf899c6aa77ac04f8bR18-R22
      success: () => {
        // 通信成功時のみ UI を確定する（登録時はモーダルも表示）
        this.sync(faved);
        this.updateCount(faved ? 1 : -1);
        if (faved) {
          this.showModal();
        }
      },
      error: (_response, _statusText, xhr) => {
        console.error(`お気に入りの更新に失敗 (${xhr?.status})`);
        // 通信失敗時はブラウザがトグルしたチェック状態を元に戻す
        this.element.checked = !faved;
      },
      complete: () => {
        // 成功・失敗を問わず再度操作できるようにする
        buttons.forEach((button) => {
          button.disabled = false;
        });
      },
    });
  }

  /**
   * @param {int} delta 負の値で減算
   * @returns
   */
  updateCount(delta) {
    if (!this.count) return;

    let x = parseInt(this.count.textContent);
    this.count.textContent = x + delta;
  }

  /**
   * お気に入り登録時に指定されたモーダルを表示する。
   * `data-modal-trigger-selector` 属性を指定した場合だけ有効。
   * 一度表示したら、同一ページ内および抑止 Cookie の有効期間（24時間）は再表示しない。
   * @returns
   */
  showModal() {
    if (!this.modalTriggerSelector) return;
    if (this.modalShownOnThisPage) return;
    if (this.modalSuppressed()) return;

    const trigger = document.querySelector(this.modalTriggerSelector);
    if (!trigger) return;

    trigger.click();
    this.modalShownOnThisPage = true;
    this.markModalShown();
  }

  /**
   * 抑止 Cookie（`data-modal-suppression-cookie`）が存在するか。
   * 存在する場合は再表示しない。
   * @returns {boolean}
   */
  modalSuppressed() {
    if (!this.modalSuppressionCookie) return false;

    return document.cookie
      .split("; ")
      .some((cookie) => cookie.startsWith(`${this.modalSuppressionCookie}=`));
  }

  /**
   * 表示済みマークの Cookie をサーバー側で設定する（他バナーと同じ仕組み）。
   * `data-modal-suppression-url` を指定した場合だけ有効。
   * @returns
   */
  markModalShown() {
    if (!this.modalSuppressionUrl) return;

    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    )?.content;
    if (!csrfToken) return;

    fetch(this.modalSuppressionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
    }).catch(() => {
      // 失敗時は Cookie が未設定のため次回再表示される（安全側）
    });
  }
}
